import React from 'react';
import {
  computeAutoApproveReconfirmUi,
  formatUtcDateMedium,
} from '../utils/autoApproveReconfirmUi.js';

/**
 * Banner for quarterly auto-approve: week-before info only, or due/grace/past-grace with Reconfirm CTA.
 * @param {object|null|undefined} apiReconfirmConfig — GET /users/channel-account-mappings `autoApproveReconfirm`
 */
export function AutoApproveReconfirmBlock({ mapping, onOpenReconfirmModal, apiReconfirmConfig }) {
  const ui = computeAutoApproveReconfirmUi(mapping, new Date(), apiReconfirmConfig);
  if (ui.phase === 'none') return null;

  const dueStr = formatUtcDateMedium(ui.dueDate);
  const graceStr = formatUtcDateMedium(ui.graceEndDate);

  if (ui.phase === 'week_before_reminder') {
    return (
      <div
        className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        role="status"
      >
        <p className="font-semibold">Auto-approve reconfirmation</p>
        <p className="mt-1 text-amber-800">
          This is separate from Terms/SMS agreement re-signing (SignWell). Here you only confirm you
          still want <span className="font-medium">automatic trade execution</span> for this channel.
        </p>
        <p className="mt-1 text-amber-800">
          Your next quarterly reconfirmation is due on <span className="font-medium">{dueStr}</span>{' '}
          (UTC calendar day — same midnight as our daily check, not your local timezone). On that date,
          use the <span className="font-medium">Reconfirm</span> button here — you do not need to turn
          Auto-approve off and on. After the due date you have{' '}
          <span className="font-medium">{ui.graceDays}</span> days of grace with periodic email/SMS reminders.
        </p>
      </div>
    );
  }

  const isPast = ui.phase === 'past_grace';
  const boxClass = isPast
    ? 'border-red-200 bg-red-50 text-red-900'
    : 'border-orange-200 bg-orange-50 text-orange-900';

  return (
    <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${boxClass}`} role="alert">
      <p className="font-semibold">{isPast ? 'Auto-approve: action needed' : 'Auto-approve: please reconfirm'}</p>
      <p className={`mt-1 ${isPast ? 'text-red-800' : 'text-orange-800'}`}>
        Not the same as Terms/SMS re-signing — only your auto-execute choice for this channel.
      </p>
      <p className={`mt-1 ${isPast ? 'text-red-800' : 'text-orange-800'}`}>
        {isPast ? (
          <>
            Reconfirmation was due <span className="font-medium">{dueStr}</span> (UTC). Grace ended{' '}
            <span className="font-medium">{graceStr}</span> (UTC). Reconfirm now to reset your cycle, or
            turn Auto-approve off in Edit if you no longer want automatic execution.
          </>
        ) : (
          <>
            Reconfirmation due <span className="font-medium">{dueStr}</span> (UTC). Grace ends{' '}
            <span className="font-medium">{graceStr}</span> (UTC), inclusive. Tap Reconfirm to record your
            choice for the next {ui.cycleDays}-day cycle without toggling Auto-approve off.
          </>
        )}
      </p>
      <button
        type="button"
        onClick={() => onOpenReconfirmModal?.(mapping)}
        className={`mt-2 inline-flex items-center rounded-md px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm ${
          isPast ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'
        }`}
      >
        Reconfirm auto-approve
      </button>
    </div>
  );
}

export function AutoApproveReconfirmModal({
  open,
  mapping,
  ui,
  onClose,
  onConfirm,
  confirming,
}) {
  if (!open || !mapping || !ui) return null;
  const dueStr = formatUtcDateMedium(ui.dueDate);
  const graceStr = formatUtcDateMedium(ui.graceEndDate);
  const title = mapping.channel_name || `Channel ${mapping.channel_id || ''}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" aria-modal="true" role="dialog">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white p-5 shadow-xl border border-gray-200">
        <h4 className="text-lg font-bold text-gray-900">Reconfirm auto-approve</h4>
        <p className="mt-1 text-sm text-gray-600">{title}</p>
        <div className="mt-4 space-y-3 text-sm text-gray-700">
          <p>
            You are reconfirming <span className="font-semibold">Auto-approve</span> for this channel
            mapping. You still approve automatic execution of qualifying trades for this mapping until the
            next {ui.cycleDays}-day cycle, unless you turn Auto-approve off in channel settings.
          </p>
          <p className="text-xs text-gray-500">
            Due (UTC): <span className="font-mono">{dueStr}</span> · Grace ends (UTC):{' '}
            <span className="font-mono">{graceStr}</span>
          </p>
          <p className="text-xs text-gray-600">
            If you do not reconfirm before grace ends, we may automatically disable Auto-approve for this
            mapping. You can always turn it off yourself or go through full activation again later. All
            due and grace dates use UTC calendar days (aligned with email reminders and the daily job).
          </p>
        </div>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={confirming}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirming}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {confirming ? 'Saving…' : 'I understand — reconfirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
